import { IncomingMessage } from 'http'
import { NextRequest } from 'next/server'

/**
 * Get the awaze client from request headers
 * @param req - The incoming request object
 * @returns The x-awaze-client header value or null if not present
 *
 * @example
 * // In API routes
 * import { NextApiRequest, NextApiResponse } from 'next'
 * import { getAwazeClient } from '../../utils/headers'
 *
 * export default function handler(req: NextApiRequest, res: NextApiResponse) {
 *   const client = getAwazeClient(req)
 *   console.log('Client:', client) // 'guest-app' or null
 * }
 *
 * @example
 * // In getServerSideProps or getInitialProps
 * import { GetServerSideProps } from 'next'
 * import { getAwazeClient } from '../utils/headers'
 *
 * export const getServerSideProps: GetServerSideProps = async ({ req }) => {
 *   const client = getAwazeClient(req)
 *   return { props: { client } }
 * }
 */
export const getAwazeClient = (req?: IncomingMessage): string | null => {
  if (!req?.headers) return null

  const clientHeader = req.headers['x-awaze-client']
  return Array.isArray(clientHeader) ? clientHeader[0] : clientHeader || null
}

/**
 * Check if the current request is from a guest app
 * @param req - The incoming request object
 * @returns true if this is a guest app request
 */
export const isGuestApp = (req?: IncomingMessage): boolean => {
  const client = getAwazeClient(req)
  return client === 'guest-app'
}

const getHostFromNextRequest = (req: NextRequest): string | null => {
  const xOriginalHost = req.headers.get('x-original-hostname')
  const xForwardedHost =
    req.headers.get('X-Forwarded-Host') ??
    req.headers.get('x-Forwarded-host') ??
    req.headers.get('x-forwarded-host')
  const hostHeader = req.headers.get('host')
  return (
    xOriginalHost ?? xForwardedHost ?? hostHeader ?? req.nextUrl.host ?? null
  )
}

const isIncomingMessage = (req: unknown): req is IncomingMessage => {
  return (
    !!req &&
    typeof req === 'object' &&
    'headers' in req &&
    typeof req.headers === 'object' &&
    !!req.headers &&
    !(req.headers instanceof Headers) &&
    !('nextUrl' in req)
  )
}

const getHostFromIncomingMessage = (req: IncomingMessage): string | null => {
  const xOriginalHost = firstOfHeaderValue(req.headers['x-original-hostname'])
  const xForwardedHost =
    firstOfHeaderValue(req.headers['X-Forwarded-Host']) ??
    firstOfHeaderValue(req.headers['x-Forwarded-host']) ??
    firstOfHeaderValue(req.headers['x-forwarded-host'])
  const hostHeader = firstOfHeaderValue(req.headers['host'])

  return xOriginalHost ?? xForwardedHost ?? hostHeader ?? null
}

export const getHost = (req: IncomingMessage | NextRequest): string | null => {
  if (!req) return null

  let host: string | null = null
  if (isIncomingMessage(req)) {
    host = getHostFromIncomingMessage(req)
  } else {
    host = getHostFromNextRequest(req)
  }

  return host ?? null
}

/**
 * Gets the first value from a header that may contain multiple comma-separated values
 * @param value - The header value(s)
 * @returns The first value if an array, or the string value, or undefined if not present
 */
export const firstOfHeaderValue = (
  value?: string | string[],
): string | undefined => {
  if (Array.isArray(value)) {
    return value.at(0)
  }
  return value
    ?.split(',')
    .map((v) => v.trim())
    .at(0)
}
